import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { ApiError, apiFetch } from '@/api/client'

export interface SessionModule {
  key: string
  label: string
  section: string
  sortOrder: number
}

export interface SessionUserInfo {
  id: string
  email: string
  displayName: string
  role: 'admin' | 'user'
}

interface MeResponse {
  user: SessionUserInfo
  modules: SessionModule[]
  uiPrefs?: Record<string, unknown>
}

interface SetupStatus {
  setupComplete: boolean
  companyName: string | null
}

export const useSessionStore = defineStore('session', () => {
  const user = ref<SessionUserInfo | null>(null)
  const modules = ref<SessionModule[]>([])
  const setupComplete = ref<boolean | null>(null)
  const companyName = ref<string | null>(null)
  const loading = ref(true)
  // §15: benutzerbezogene UI-Einstellungen (server-seitig, geräteübergreifend).
  const uiPrefs = ref<Record<string, unknown>>({})

  const isAuthenticated = computed(() => user.value !== null)
  const isAdmin = computed(() => user.value?.role === 'admin')

  /** Navigation: nach Sektionen gruppiert, Reihenfolge kommt vom Server. */
  const navigation = computed(() => {
    const sections = new Map<string, SessionModule[]>()
    for (const m of [...modules.value].sort((a, b) => a.sortOrder - b.sortOrder)) {
      const list = sections.get(m.section) ?? []
      list.push(m)
      sections.set(m.section, list)
    }
    return [...sections.entries()].map(([section, items]) => ({ section, items }))
  })

  function hasModule(key: string): boolean {
    return modules.value.some((m) => m.key === key)
  }

  async function load(): Promise<void> {
    loading.value = true
    try {
      const status = await apiFetch<SetupStatus>('/setup/status')
      setupComplete.value = status.setupComplete
      companyName.value = status.companyName

      if (!status.setupComplete) {
        user.value = null
        modules.value = []
        return
      }

      const me = await apiFetch<MeResponse>('/auth/me')
      user.value = me.user
      modules.value = me.modules
      uiPrefs.value = me.uiPrefs ?? {}
    } catch (err) {
      // 401 ist der Normalfall vor dem Login, kein Fehler.
      if (!(err instanceof ApiError) || err.status !== 401) {
        console.error('Sitzung konnte nicht geladen werden', err)
      }
      user.value = null
      modules.value = []
    } finally {
      loading.value = false
    }
  }

  async function logout(): Promise<void> {
    await apiFetch('/auth/logout', { method: 'POST' })
    user.value = null
    modules.value = []
    uiPrefs.value = {}
  }

  /** Eine UI-Einstellung lesen (mit Default, falls noch nicht gesetzt). */
  function pref<T>(key: string, fallback: T): T {
    const v = uiPrefs.value[key]
    return v === undefined ? fallback : (v as T)
  }
  /** Eine UI-Einstellung setzen: sofort lokal, server-seitig gemerged (best effort). */
  async function setPref(key: string, value: unknown): Promise<void> {
    uiPrefs.value = { ...uiPrefs.value, [key]: value }
    try {
      const res = await apiFetch<{ uiPrefs: Record<string, unknown> }>('/me/ui-prefs', { method: 'PATCH', body: { [key]: value } })
      uiPrefs.value = res.uiPrefs
    } catch { /* rein kosmetisch – lokaler Stand bleibt bestehen */ }
  }

  return {
    user,
    modules,
    setupComplete,
    companyName,
    loading,
    uiPrefs,
    isAuthenticated,
    isAdmin,
    navigation,
    hasModule,
    load,
    logout,
    pref,
    setPref,
  }
})
